import * as React from 'react';

import {
  buildDayOptions,
  buildHalfHourPairRows,
  buildHourOptions,
  buildSixtyBasedOptions,
  buildTimeOptionKey,
  chunkOptions,
  deriveMinuteSecondSteps,
  displayHour,
  formatTime,
  getHour24WithPeriod,
  getPeriod,
  getStepSeconds,
  getTimelineStepSeconds,
  hasTimeValue,
  isFourColumnHalfHourPairing,
  isTimeComplete,
  parseTime,
  timePartsReducer,
  type TimeFieldSegmentKey,
  type TimeOption,
  type TimeParts,
  type TimePopoverLayout,
  type TimePrecision,
} from './time-utils';

type UseTimePickerOptions = {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onTimePartsChange?: (parts: TimeParts) => void;
  onRequestClose?: () => void;
  step?: number | string;
  use12Hour?: boolean;
  precision?: TimePrecision;
  popoverLayout?: TimePopoverLayout;
  parts?: TimeParts;
};

type TimePickerController = {
  precision: TimePrecision;
  use12Hour: boolean;
  popoverLayout: TimePopoverLayout;
  timeParts: TimeParts;
  isInputFilled: boolean;
  scrollTrigger: number;
  onPopoverOpenChange: (nextOpen: boolean) => void;
  clear: () => void;
  clearSegment: (segment: TimeFieldSegmentKey) => void;
  dayOptions: TimeOption[];
  gridRows: TimeOption[][];
  fourColumnHeaderGroups?: [string, string];
  dayOptionKey?: string;
  showSingleColumnList: boolean;
  showFourColumnGrid: boolean;
  currentDisplayHour?: number;
  hours: number[];
  minutes: number[];
  seconds: number[];
  selectOption: (parts: TimeParts) => void;
  selectHour: (option: number | string) => void;
  updatePeriod: (period: 'AM' | 'PM') => void;
  commit: (nextParts: TimeParts) => void;
};

function useTimePicker({
  value,
  defaultValue,
  onValueChange,
  onTimePartsChange,
  onRequestClose,
  step = 1,
  use12Hour = false,
  precision = 'second',
  popoverLayout = 'columns',
  parts,
}: UseTimePickerOptions): TimePickerController {
  const [timeParts, dispatchTimeParts] = React.useReducer(
    timePartsReducer,
    parseTime(value ?? defaultValue) ?? {}
  );
  const [scrollTrigger, setScrollTrigger] = React.useState(0);

  const stepSeconds = getStepSeconds(step);
  const { minuteStep, secondStep } = deriveMinuteSecondSteps(stepSeconds);
  const hours = buildHourOptions(use12Hour);
  const minutes = buildSixtyBasedOptions(minuteStep);
  const seconds = buildSixtyBasedOptions(secondStep);

  React.useEffect(() => {
    if (value === undefined) return;
    const nextParts = parseTime(value);
    if (nextParts) {
      dispatchTimeParts({ type: 'set', parts: nextParts });
    } else {
      dispatchTimeParts({ type: 'clear' });
    }
  }, [value]);

  React.useEffect(() => {
    if (parts === undefined) return;
    dispatchTimeParts({ type: 'set', parts });
    // Re-sync on the individual fields rather than the `parts` object identity, since
    // callers typically construct a new object every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parts?.hour24,
    parts?.minute,
    parts?.second,
    parts?.hour12,
    parts?.period,
  ]);

  const commit = React.useCallback(
    (nextParts: TimeParts) => {
      dispatchTimeParts({ type: 'set', parts: nextParts });
      onTimePartsChange?.(nextParts);
      if (isTimeComplete(nextParts, precision)) {
        onValueChange?.(formatTime(nextParts, use12Hour, precision));
      } else {
        onValueChange?.('');
      }
    },
    [onValueChange, onTimePartsChange, precision, use12Hour]
  );

  const clear = React.useCallback(() => {
    dispatchTimeParts({ type: 'clear' });
    onTimePartsChange?.({});
    onValueChange?.('');
  }, [onValueChange, onTimePartsChange]);

  // Clears a single column's segment (columns layout only) rather than the whole time,
  // so deselecting Hour doesn't also wipe out an already-picked Minute/Second/Period.
  const clearSegment = React.useCallback(
    (segment: TimeFieldSegmentKey) => {
      const next: TimeParts = { ...timeParts };
      if (segment === 'hour') {
        next.hour24 = undefined;
        next.hour12 = undefined;
      } else if (segment === 'minute') {
        next.minute = undefined;
      } else if (segment === 'second') {
        next.second = undefined;
      } else if (segment === 'period') {
        // Without AM/PM, a 12-hour value can't resolve to a 24-hour value either.
        next.period = undefined;
        next.hour24 = undefined;
      }
      commit(next);
    },
    [commit, timeParts]
  );

  const updatePeriod = React.useCallback(
    (nextPeriod: 'AM' | 'PM') => {
      const hour12 =
        timeParts.hour24 !== undefined
          ? displayHour(timeParts.hour24, true)
          : timeParts.hour12;
      commit({
        ...timeParts,
        period: nextPeriod,
        hour24:
          hour12 !== undefined
            ? getHour24WithPeriod(hour12, nextPeriod)
            : undefined,
      });
    },
    [commit, timeParts]
  );

  const selectHour = React.useCallback(
    (option: number | string) => {
      const selectedHour = Number(option);
      if (!use12Hour) {
        commit({ ...timeParts, hour24: selectedHour });
        return;
      }

      const period =
        timeParts.period ??
        (timeParts.hour24 !== undefined
          ? getPeriod(timeParts.hour24)
          : undefined);
      commit({
        ...timeParts,
        hour12: selectedHour,
        hour24:
          period !== undefined
            ? getHour24WithPeriod(selectedHour, period)
            : undefined,
      });
    },
    [commit, timeParts, use12Hour]
  );

  const currentDisplayHour =
    timeParts.hour24 !== undefined
      ? displayHour(timeParts.hour24, use12Hour)
      : timeParts.hour12;
  const isInputFilled = hasTimeValue(timeParts);
  const timelineStepSeconds = getTimelineStepSeconds(
    precision,
    stepSeconds,
    minuteStep
  );
  const dayOptionKey =
    timeParts.hour24 === undefined
      ? undefined
      : buildTimeOptionKey(
          timeParts.hour24,
          timeParts.minute ?? 0,
          precision === 'second' ? (timeParts.second ?? 0) : 0
        );
  const dayOptions = React.useMemo(
    () => buildDayOptions(precision, use12Hour, timelineStepSeconds),
    [precision, use12Hour, timelineStepSeconds]
  );
  const useFourColumnHalfHourPairing = isFourColumnHalfHourPairing(
    popoverLayout,
    precision,
    timelineStepSeconds
  );

  const gridRows = React.useMemo(() => {
    if (useFourColumnHalfHourPairing) {
      return buildHalfHourPairRows(use12Hour);
    }

    return chunkOptions(dayOptions, 4);
  }, [dayOptions, useFourColumnHalfHourPairing, use12Hour]);

  const fourColumnHeaderGroups = React.useMemo(() => {
    if (!useFourColumnHalfHourPairing) return undefined;
    return use12Hour
      ? (['AM', 'PM'] as [string, string])
      : (['00-11', '12-23'] as [string, string]);
  }, [useFourColumnHalfHourPairing, use12Hour]);

  const selectOption = React.useCallback(
    (parts: TimeParts) => {
      const nextParts: TimeParts = {
        ...timeParts,
        hour24: parts.hour24,
      };

      if (precision === 'hour') {
        nextParts.minute = 0;
        nextParts.second = 0;
      }

      if (precision === 'minute') {
        nextParts.minute = parts.minute ?? 0;
        nextParts.second = 0;
      }

      if (precision === 'second') {
        nextParts.minute = parts.minute ?? 0;
        nextParts.second = parts.second ?? 0;
      }

      commit(nextParts);
      onRequestClose?.();
    },
    [commit, onRequestClose, precision, timeParts]
  );

  return {
    precision,
    use12Hour,
    popoverLayout,
    timeParts,
    isInputFilled,
    scrollTrigger,
    onPopoverOpenChange: (nextOpen: boolean) => {
      if (nextOpen) {
        setScrollTrigger((current) => current + 1);
      }
    },
    clear,
    clearSegment,
    dayOptions,
    gridRows,
    fourColumnHeaderGroups,
    dayOptionKey,
    showSingleColumnList: popoverLayout === 'single-column-list',
    showFourColumnGrid: popoverLayout === 'four-column-grid',
    currentDisplayHour,
    hours,
    minutes,
    seconds,
    selectOption,
    selectHour,
    updatePeriod,
    commit,
  };
}

export { useTimePicker };
export type {
  TimePickerController,
  TimePopoverLayout,
  TimePrecision,
  UseTimePickerOptions,
};
